import api from 'api'
import {
  CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST,
  CACHE_TAG_EVENTS_VERSION_LIST,
  CACHE_TAG_ACCOUNTS_VERSION_LIST,
  CACHE_TAG_ADDRESS_VERSION_LIST,
  CACHE_TAG_TAG_REF_VERSION_LIST,
  CACHE_TAG_CONTACT_VERSION_LIST,
  CACHE_TAG_CONTACT_VERSION_REFS,
  CACHE_TAG_WORK_ORDER_VERSION_LIST,
  CACHE_TAG_DISPOSAL_TICKET_VERSION_LIST,
  CACHE_TAG_EXCEPTION_VERSION_LIST,
  CACHE_TAG_WORK_ORDER_NOTE_VERSION_LIST,
  CACHE_TAG_WORK_ORDER_CONTAINER_VERSION_LIST,
  CACHE_TAG_WORK_ORDER_FEE_TAX_LINE_VERSION_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_EVENT_VERSION_LIST,
  CACHE_TAG_INVOICE_VERSION_LIST,
  CACHE_TAG_RENTAL_FEE_TO_CS_VERSION_LIST,
  CACHE_TAG_RENTAL_PERIOD_VERSION_LIST,
  CACHE_TAG_SUSPENSION_PRORATION_VERSION_LIST,
  CACHE_TAG_SUSPENSION_AUTOMATION_VERSION_LIST,
  CACHE_TAG_RENTAL_BILLING_SETTINGS_VERSION_LIST,
  CACHE_TAG_BILLING_PROFILE_VERSION_LIST,
  CACHE_TAG_LATE_FEE_VERSION_LIST,
} from 'api/cacheTagTypes'
import { withIdTag } from 'utils/cache'

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    getConfiguredServiceVersions: build.query({
      query: ({ id }) => ({
        url: `/api/v1/versions/configured-service/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_VERSION_LIST, arg?.id),
    }),
    getEventConfiguredServiceVersions: build.query({
      query: ({ csId, csEventId }) => ({
        url: `/api/v1/versions/configured-service/${csId}/event/${csEventId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_VERSION_LIST, arg?.csEventId),
    }),
    getWorkOrderVersionsByConfiguredService: build.query({
      query: ({ id }) => ({
        url: `/api/v1/versions/configured-service/${id}/work-orders`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_WORK_ORDER_VERSION_LIST, arg?.id),
    }),
    getServiceEventsVersions: build.query({
      query: ({ id }) => ({
        url: `/api/v1/versions/configured-service/${id}/events`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
    }),
    getAccountDetailsVersions: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_ACCOUNTS_VERSION_LIST, arg?.accountId),
    }),
    getTagRefsVersions: build.query({
      query: ({ id }) => ({
        url: `/api/v1/versions/tag/${id}/tag-refs`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_TAG_REF_VERSION_LIST, arg?.id),
    }),
    getContactVersionReferencesByAccountId: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/contact-refs`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONTACT_VERSION_REFS, arg?.accountId),
    }),
    getContactVersionsById: build.query({
      query: ({ contactId }) => ({
        url: `/api/v1/versions/contact/${contactId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONTACT_VERSION_LIST, arg?.contactId),
    }),
    getAddressVersions: build.query({
      query: () => ({
        url: `/api/v1/versions/address`,
      }),
    }),
    getAddressVersionsById: build.query({
      query: ({ addressId }) => ({
        url: `/api/v1/versions/address/${addressId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_ADDRESS_VERSION_LIST, arg?.addressId),
    }),
    getWOVersions: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_WORK_ORDER_VERSION_LIST, arg?.woId),
    }),
    getDisposalTicketVersionsByWoId: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}/disposal-tickets`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_DISPOSAL_TICKET_VERSION_LIST, arg?.woId),
    }),
    getExceptionVersionsByWoId: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}/exceptions`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_EXCEPTION_VERSION_LIST, arg?.woId),
    }),
    // ContainerToWorkOrder link revisions — feeds the "Removed/Restored container"
    // activity rows. 404s harmlessly until the BE sub-endpoint deploys.
    getContainerVersionsByWoId: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}/containers`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_WORK_ORDER_CONTAINER_VERSION_LIST, arg?.woId),
    }),
    getWONoteVersions: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}/notes`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_WORK_ORDER_NOTE_VERSION_LIST, arg?.woId),
    }),
    getFeeTaxLineVersionsByWoId: build.query({
      query: ({ woId }) => ({
        url: `/api/v1/versions/work-order/${woId}/fee-tax-lines`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_WORK_ORDER_FEE_TAX_LINE_VERSION_LIST, arg?.woId),
    }),
    getInvoiceVersions: build.query({
      query: ({ invoiceId }) => ({
        url: `/api/v1/versions/invoice/${invoiceId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_INVOICE_VERSION_LIST, arg?.invoiceId),
    }),
    getRentalPeriodVersions: build.query({
      query: ({ rentalPeriodId }) => ({
        url: `/api/v1/versions/rental-period/${rentalPeriodId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_RENTAL_PERIOD_VERSION_LIST, arg?.rentalPeriodId),
    }),
    getAllRentalPeriodVersions: build.query({
      query: ({ rentalFeeToConfiguredServiceId }) => ({
        url: `/api/v1/versions/rental-fee-to-configured-service/${rentalFeeToConfiguredServiceId}/rental-periods`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_RENTAL_PERIOD_VERSION_LIST, arg?.rentalFeeToConfiguredServiceId),
    }),
    getSuspensionProrationVersions: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/suspension-proration`,
        method: 'GET',
      }),
      transformResponse: res => res?.versions ?? [],
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_SUSPENSION_PRORATION_VERSION_LIST, arg?.accountId),
    }),
    getSuspensionAutomationVersions: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/suspension-automation`,
        method: 'GET',
      }),
      transformResponse: res => res?.versions ?? [],
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_SUSPENSION_AUTOMATION_VERSION_LIST, arg?.accountId),
    }),
    getRentalBillingSettingsVersions: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/rental-billing-settings`,
        method: 'GET',
      }),
      transformResponse: res => res?.versions ?? [],
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_RENTAL_BILLING_SETTINGS_VERSION_LIST, arg?.accountId),
    }),
    getRentalFeeToConfiguredServiceVersions: build.query({
      query: ({ rentalFeeToConfiguredServiceId }) => ({
        url: `/api/v1/versions/rental-fee-to-configured-service/${rentalFeeToConfiguredServiceId}`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_RENTAL_FEE_TO_CS_VERSION_LIST, arg?.rentalFeeToConfiguredServiceId),
    }),
    getBillingProfileVersions: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/billing`,
        method: 'GET',
      }),
      transformResponse: res => res?.versions ?? [],
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_BILLING_PROFILE_VERSION_LIST, arg?.accountId),
    }),
    getLateFeeVersionsByAccountId: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/late-fees`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_LATE_FEE_VERSION_LIST, arg?.accountId),
    }),
    // Tagged as the account's own history, which every deactivation and reactivation already invalidates.
    getLifecycleEventVersionsByAccountId: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/versions/account/${accountId}/lifecycle-events`,
        method: 'GET',
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_ACCOUNTS_VERSION_LIST, arg?.accountId),
    }),
  }),
})

export const {
  useLazyGetConfiguredServiceVersionsQuery,
  useLazyGetEventConfiguredServiceVersionsQuery,
  useLazyGetContainerVersionsQuery,
  useLazyGetServiceEventsVersionsQuery,
  useLazyGetWorkOrderVersionsByConfiguredServiceQuery,
  useLazyGetAccountDetailsVersionsQuery,
  useLazyGetTagRefsVersionsQuery,
  useLazyGetAddressVersionsQuery,
  useLazyGetAddressVersionsByIdQuery,
  useLazyGetContactVersionReferencesByAccountIdQuery,
  useLazyGetContactVersionsByIdQuery,
  useLazyGetWOVersionsQuery,
  useLazyGetDisposalTicketVersionsByWoIdQuery,
  useGetExceptionVersionsByWoIdQuery,
  useLazyGetExceptionVersionsByWoIdQuery,
  useLazyGetContainerVersionsByWoIdQuery,
  useLazyGetWONoteVersionsQuery,
  useLazyGetFeeTaxLineVersionsByWoIdQuery,
  useLazyGetInvoiceVersionsQuery,
  useLazyGetRentalPeriodVersionsQuery,
  useGetAllRentalPeriodVersionsQuery,
  useLazyGetSuspensionProrationVersionsQuery,
  useLazyGetSuspensionAutomationVersionsQuery,
  useLazyGetRentalBillingSettingsVersionsQuery,
  useGetRentalFeeToConfiguredServiceVersionsQuery,
  useLazyGetBillingProfileVersionsQuery,
  useLazyGetLateFeeVersionsByAccountIdQuery,
  useLazyGetLifecycleEventVersionsByAccountIdQuery,
} = extendedApi
